import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CartAmountValidator } from '@next/core-lib/cart';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';

import { Subject, debounceTime, shareReplay, takeUntil } from 'rxjs';

import { TreatmentTypes } from 'src/app/overview/models';

import { v4 as uuidv4 } from 'uuid';

import * as OverviewSelectors from '../../../store/selectors';
import { FertilisersOrderplanDataSource } from '../../datasource/fertilisers-orderplan.datasource';
import {
  CropAid,
  DeliveryPeriodMomentType,
  FertiliserOrderplan,
  FertilisersOverviewColumnTypes,
  FertilizerStockRow,
  OrderLine,
  OrderLineStatus,
  Package,
  Product,
  displayColumns,
} from '../../models';
import { DeleteOrderLine, SaveCustomerFertilizerStock, SaveOrderLine } from '../../store/actions/fertilisers-actions';
import {
  selectArticlesByCropAids,
  selectCustomerFertilizerStock,
  selectFertiliserOrderplan,
  selectNonOrderableFertilisers,
  selectProprietaryFertilisers,
} from '../../store/selectors/fertiliser.selectors';

@Component({
  selector: 'app-overview-table',
  templateUrl: './overview-table.component.html',
  styleUrls: ['./overview-table.component.scss'],
  standalone: false,
})
export class OverviewTableComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  readonly locale = inject(LocaleService);

  @Input() columnsToDisplay: string[] = [];
  @Input() tableType: 'orderable' | 'non-orderable' | 'proprietary-fertilizer';
  @Output() togglePrimaryActionTableButton: EventEmitter<boolean> = new EventEmitter();

  cropAids: CropAid[];
  packages: Package[];
  cropAidArticles: Product[];
  customerFertilizerStock: FertilizerStockRow[];
  amountFormGroup: FormGroup;

  fertiliserOrderplan$ = this.store.pipe(select(selectFertiliserOrderplan));
  nonOrderableFertilisers$ = this.store.pipe(select(selectNonOrderableFertilisers));
  proprietaryFertilisers$ = this.store.pipe(select(selectProprietaryFertilisers));

  readonly dataSource: FertilisersOrderplanDataSource;

  private readonly destroyed$ = new Subject<boolean>();

  constructor() {
    this.dataSource = new FertilisersOrderplanDataSource(this.store);
  }

  ngOnInit(): void {
    this.getCropAids();
    this.getPackages();
    this.getArticlesByCropAids();
    this.getCustomerFertilizerStock();
  }

  isCropAidActive(orderline: FertiliserOrderplan): boolean {
    const cropAid = this.cropAids.find((c) => c.id === orderline.cropAidId);
    return cropAid?.isActive;
  }

  cropAidHasManyArticles(orderline: FertiliserOrderplan): boolean {
    const articles = this.cropAidArticles.filter((article) => article.id === orderline.cropAidId);
    return articles.length > 1;
  }

  cropAidHasNoArticles(orderline: FertiliserOrderplan): boolean {
    const articles = this.cropAidArticles.filter((article) => article.id === orderline.cropAidId);
    return articles.length === 0;
  }

  allArticlesShown(orderline: FertiliserOrderplan): boolean {
    const allCropAidArticles = this.cropAidArticles.filter((article) => article.id === orderline.cropAidId);
    const shownCropAidArticles = this.dataSource.data$.value.filter(
      (fertiliserOrderline) => fertiliserOrderline.cropAidWithArticles && fertiliserOrderline.cropAidWithArticles.id === orderline.cropAidId,
    );

    return allCropAidArticles.length === shownCropAidArticles.length;
  }

  onCropAidArticleChange(articleNumber: string, orderPlan: FertiliserOrderplan) {
    const article = this.cropAidArticles.find((cropAid) => cropAid.articleNumber === articleNumber);

    const orderLine = this.getOrderLineFromOrderPlan(orderPlan, article.packageId);
    orderLine.cropAidId = article.id;
    orderLine.count = 0;
    this.saveOrderLine(orderLine);
  }

  getPackageSteps(increaseOrderUnitQuantity: number) {
    return increaseOrderUnitQuantity ?? 1;
  }

  onAmountChanged(orderPlan: FertiliserOrderplan) {
    if ((orderPlan.orderLineId || orderPlan.cropAidWithArticles != null) && +orderPlan.amount === 0) {
      setTimeout(() => {
        this.store.dispatch(new DeleteOrderLine(orderPlan.orderLineId));
      }, 1000);
    }

    if ((orderPlan.orderLineId || orderPlan.cropAidWithArticles != null) && +orderPlan.amount > 0) {
      const orderLine = this.getOrderLineFromOrderPlan(orderPlan, orderPlan.packageId);
      orderLine.count = +orderPlan.amount;
      this.saveOrderLine(orderLine);
    }
  }

  onFertilizerStockChanged(amount: number, cropAidId: number, orderLineId: number, orderPlan?: FertiliserOrderplan) {
    if (amount === null || amount === undefined) {
      return;
    }

    if (amount < 0) {
      if (orderPlan) {
        orderPlan.stockAmount = this.customerFertilizerStock.find((x) => x.cropAidId === cropAidId)?.stock ?? 0;
      }
      return;
    }

    const existingCustomerStock = this.customerFertilizerStock.find((x) => x.cropAidId === cropAidId);
    if (existingCustomerStock) {
      if (amount === existingCustomerStock.stock) {
        return;
      }
      existingCustomerStock.stock = amount;
    } else {
      const fertilizerStockRow: FertilizerStockRow = {
        cropAidId,
        stock: amount,
      };

      this.customerFertilizerStock.push(fertilizerStockRow);
    }
    this.store.dispatch(new SaveCustomerFertilizerStock(this.customerFertilizerStock, orderLineId));
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  addNewOrderPlanRow(orderPlan: FertiliserOrderplan) {
    const existingArticlesInOrderplan = this.dataSource.data$.value.filter(
      (fertiliserOrderline) => fertiliserOrderline.cropAidWithArticles && fertiliserOrderline.cropAidWithArticles.id === orderPlan.cropAidId,
    );

    const article = this.cropAidArticles.find(
      (cropAid) =>
        cropAid.id === orderPlan.cropAidId && existingArticlesInOrderplan.find((x) => x.cropAidWithArticles.articleNumber === cropAid.articleNumber) == null,
    );

    const orderLine = this.getEmptyOrderLine();
    // Get the package id that is not already in the orderplan
    orderLine.packageId = article.packageId;

    this.store.dispatch(new SaveOrderLine(orderLine, orderPlan.cropAidId));
  }

  isTableOrderable() {
    return this.tableType === 'orderable';
  }

  private getCropAids() {
    this.store.pipe(takeUntil(this.destroyed$), select(OverviewSelectors.selectCropAids)).subscribe((result) => (this.cropAids = result));
  }

  private getPackages() {
    this.store.pipe(takeUntil(this.destroyed$), select(OverviewSelectors.selectPackages)).subscribe((result) => (this.packages = result));
  }

  private getOrderableFertilisers() {
    this.fertiliserOrderplan$
      .pipe(
        debounceTime(100), // needed because the location selector is emitted mutiple times
        takeUntil(this.destroyed$),
      )
      .subscribe((result) => {
        if (result) {
          const resultWithControl = this.generateFromGroup(result);

          this.dataSource.data$.next(resultWithControl);
        }
      });
  }

  private generateFromGroup(orderplan: FertiliserOrderplan[]) {
    const group: { [key: string]: AbstractControl } = {};
    const orderplanWithControls = orderplan.map((fert) => {
      const formControl = new FormControl(fert.amount);

      this.decorateFormControl(fert, formControl);

      formControl.valueChanges.pipe(debounceTime(1000)).subscribe((value) => {
        // This covers both saving and removing an order line
        if (formControl.valid || (formControl.invalid && value === 0)) {
          fert.amount = value;
          this.onAmountChanged(fert);

          // When an orderline is removed the amount is set to 0
          // This is not normally valid and because of it the "To shopping cart" button is disabled
          // To make it work we temporary remove the validation when the orderline is removed
          if (formControl.invalid && value === 0) {
            formControl.clearValidators();
            formControl.updateValueAndValidity();
          }
        }
      });

      const id = uuidv4();
      group[id] = formControl;

      return { ...fert, amountControl: formControl, amountControlId: id };
    });
    this.amountFormGroup = new FormGroup(group);

    this.amountFormGroup.valueChanges.subscribe((_) => {
      this.togglePrimaryActionTableButton.next(!this.amountFormGroup.valid);
    });

    return orderplanWithControls;
  }

  private decorateFormControl(orderInfo: FertiliserOrderplan, formControl: FormControl): void {
    const validators = Array<ValidatorFn>();
    validators.push(Validators.min(orderInfo.minimumOrderUnitQuantity ?? 1));
    validators.push(Validators.required);
    if (orderInfo.maximumOrderUnitQuantity) {
      validators.push(Validators.max(orderInfo.maximumOrderUnitQuantity));
    }
    if (orderInfo.minimumOrderUnitQuantity && orderInfo.increaseOrderUnitQuantity) {
      const validator = new CartAmountValidator(
        orderInfo.minimumOrderUnitQuantity,
        orderInfo.maximumOrderUnitQuantity,
        orderInfo.increaseOrderUnitQuantity,
        this.locale,
      );
      validators.push((control: AbstractControl) => validator.validate(control));
    }
    formControl.setValidators(validators);
  }

  private getNonOrderableFertilisers() {
    this.nonOrderableFertilisers$
      .pipe(
        debounceTime(100), // needed because the location selector is emitted mutiple times
        takeUntil(this.destroyed$),
        shareReplay(1),
      )
      .subscribe((result) => {
        if (result) {
          this.dataSource.data$.next(result);
        }
      });
  }

  private getProprietaryFertilisers() {
    this.proprietaryFertilisers$
      .pipe(
        debounceTime(100), // needed because the location selector is emitted mutiple times
        takeUntil(this.destroyed$),
        shareReplay(1),
      )
      .subscribe((result) => {
        if (result) {
          this.dataSource.data$.next(result);
        }
      });
  }

  private saveOrderLine(orderLine: OrderLine) {
    this.store.dispatch(new SaveOrderLine(orderLine, orderLine.cropAidId));
  }

  private getArticlesByCropAids() {
    this.store
      .select(selectArticlesByCropAids)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((articles) => {
        this.cropAidArticles = articles;
        if (this.tableType === 'orderable') {
          this.getOrderableFertilisers();
          this.columnsToDisplay = [...displayColumns];
        } else if (this.tableType === 'non-orderable') {
          this.getNonOrderableFertilisers();
          this.setColumnsToDisplayForNonOrderableProducts();
        } else if (this.tableType === 'proprietary-fertilizer') {
          this.getProprietaryFertilisers();
          this.setColumnsToDisplayForNonOrderableProducts();
        }
      });
  }

  private getCustomerFertilizerStock() {
    this.store
      .select(selectCustomerFertilizerStock)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((stocks) => {
        this.customerFertilizerStock = stocks;
      });
  }

  private setColumnsToDisplayForNonOrderableProducts() {
    const columnsToExclude: string[] = [FertilisersOverviewColumnTypes.orderedAmount, FertilisersOverviewColumnTypes.result];
    this.columnsToDisplay = this.columnsToDisplay.filter((column) => !columnsToExclude.includes(column));
  }

  private getOrderLineFromOrderPlan(orderPlan: FertiliserOrderplan, packageId: number): OrderLine {
    return {
      id: orderPlan.orderLineId ?? -1,
      packageId,
      cropAidId: orderPlan.cropAidId,
      count: orderPlan.amount ?? 0,
      year: null,
      periodId: null,
      status: OrderLineStatus.open,
      deliveryPeriodMoment: DeliveryPeriodMomentType.empty,
      treatmentType: TreatmentTypes.Fertilization,
      orderedDate: null,
      shipToId: null,
      addedByOptimizer: false,
      cartId: -1,
    };
  }

  private getEmptyOrderLine(): OrderLine {
    return {
      id: -1,
      packageId: null,
      count: 0,
      year: null,
      periodId: null,
      status: OrderLineStatus.open,
      deliveryPeriodMoment: DeliveryPeriodMomentType.empty,
      treatmentType: TreatmentTypes.Fertilization,
      orderedDate: null,
      shipToId: 1,
      addedByOptimizer: false,
      cartId: -1,
    };
  }
}
